/*
 * /patient/health-records — read-only health summary for the logged-in patient
 */
import { cn, Container, Page, PageHeader, Placeholder } from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import {
	Activity,
	CalendarDays,
	FileText,
	FlaskConical,
	Pill,
	Stethoscope,
} from "lucide-react"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/patient/health-records")({
	component: PatientHealthRecordsPage,
	loader: rolePageLoader(["patient"]),
})

function PatientHealthRecordsPage() {
	const { data: patient } = trpc.patient.getByFilter.useQuery({ where: { active: true } })
	const patientId = patient?.id

	const { data: prescriptions, isLoading: rxLoading } = trpc.prescription.getAllByFilter.useQuery(
		{ where: { patientId: patientId!, active: true } },
		{ enabled: !!patientId },
	)

	const { data: diagnoses, isLoading: dxLoading } = trpc.patientDiagnosis.getAllByFilter.useQuery(
		{ where: { patientId: patientId!, active: true } },
		{ enabled: !!patientId },
	)

	const { data: treatmentPlans, isLoading: tpLoading } = trpc.treatmentPlan.getAllByFilter.useQuery(
		{ where: { patientId: patientId!, active: true } },
		{ enabled: !!patientId },
	)

	const latestRx = prescriptions
		?.filter((r) => (r as any).isLatest)
		.sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime())[0]
		?? prescriptions?.sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime())[0]

	return (
		<Page>
			<Container>
				<PageHeader title="Health Records" />

				<div className="grid gap-6 md:grid-cols-2">
					{/* ─── Current Prescription ─────────────────────────────── */}
					<Section
						icon={<Pill className="w-4 h-4" />}
						title="Current Prescription"
						loading={rxLoading}
					>
						{!latestRx ? (
							<Placeholder title="No active prescription" />
						) : (
							<div className="space-y-2 text-sm">
								<Row label="Medication" value={(latestRx as any).medicationName ?? (latestRx as any).drug ?? "—"} />
								<Row label="Dosage" value={(latestRx as any).dosage ?? "—"} />
								<Row label="Frequency" value={(latestRx as any).frequency ?? "—"} />
								{(latestRx as any).startDate && (
									<Row label="Start" value={fmtDate((latestRx as any).startDate)} />
								)}
								{(latestRx as any).endDate && (
									<Row
										label="Expires"
										value={fmtDate((latestRx as any).endDate)}
										highlight={isExpiringSoon((latestRx as any).endDate)}
									/>
								)}
								{(latestRx as any).instructions && (
									<div className="pt-1 text-muted-foreground border-t">
										<p className="text-xs font-medium uppercase tracking-wide mb-1">Instructions</p>
										<p className="whitespace-pre-wrap">{(latestRx as any).instructions}</p>
									</div>
								)}
							</div>
						)}
					</Section>

					{/* ─── Diagnoses ────────────────────────────────────────── */}
					<Section
						icon={<Stethoscope className="w-4 h-4" />}
						title="Diagnoses"
						loading={dxLoading}
					>
						{!diagnoses?.length ? (
							<Placeholder title="No diagnoses on record" />
						) : (
							<ul className="space-y-2 text-sm">
								{diagnoses.map((dx) => (
									<li key={dx.id} className="flex items-start gap-2">
										<Activity className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
										<div>
											<p className="font-medium">
												{(dx as any).diagnosisCode ?? (dx as any).code ?? ""}
												{((dx as any).diagnosisName ?? (dx as any).name) && (
													<span className="font-normal text-muted-foreground ml-1.5">
														— {(dx as any).diagnosisName ?? (dx as any).name}
													</span>
												)}
											</p>
											{(dx as any).type && (
												<p className="text-xs text-muted-foreground capitalize">{(dx as any).type}</p>
											)}
										</div>
									</li>
								))}
							</ul>
						)}
					</Section>

					{/* ─── Treatment Plans ──────────────────────────────────── */}
					<Section
						icon={<FileText className="w-4 h-4" />}
						title="Treatment Plans"
						loading={tpLoading}
						className="md:col-span-2"
					>
						{!treatmentPlans?.length ? (
							<Placeholder title="No treatment plans on record" />
						) : (
							<div className="grid gap-3 sm:grid-cols-2">
								{treatmentPlans.map((tp) => (
									<div key={tp.id} className="rounded-lg border bg-muted/40 px-4 py-3 text-sm space-y-1">
										{(tp as any).title && (
											<p className="font-medium">{(tp as any).title}</p>
										)}
										{(tp as any).description && (
											<p className="text-muted-foreground text-xs whitespace-pre-wrap line-clamp-4">
												{(tp as any).description}
											</p>
										)}
										{tp.createdAt && (
											<p className="text-xs text-muted-foreground flex items-center gap-1 pt-1">
												<CalendarDays className="w-3 h-3" />
												{fmtDate(tp.createdAt)}
											</p>
										)}
									</div>
								))}
							</div>
						)}
					</Section>

					{/* ─── Allergies / vitals from patient record ───────────── */}
					{patient && (patient.allergies || (patient as any).bloodType) && (
						<Section icon={<FlaskConical className="w-4 h-4" />} title="Medical Info" className="md:col-span-2">
							<div className="flex flex-wrap gap-6 text-sm">
								{patient.allergies && (
									<Row
										label="Allergies"
										value={patient.allergies}
										highlight={patient.allergies.toLowerCase() !== "none"}
									/>
								)}
								{(patient as any).bloodType && (
									<Row label="Blood Type" value={(patient as any).bloodType} />
								)}
							</div>
						</Section>
					)}
				</div>
			</Container>
		</Page>
	)
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

function Section({
	icon,
	title,
	loading,
	children,
	className,
}: {
	icon: React.ReactNode
	title: string
	loading?: boolean
	children: React.ReactNode
	className?: string
}) {
	return (
		<div className={cn("rounded-xl border bg-card p-5 space-y-4", className)}>
			<div className="flex items-center gap-2 text-sm font-semibold">
				<span className="text-primary">{icon}</span>
				{title}
			</div>
			{loading ? (
				<Placeholder title="Loading…" />
			) : (
				children
			)}
		</div>
	)
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
	return (
		<div className="flex items-baseline gap-2">
			<span className="text-xs text-muted-foreground w-20 shrink-0">{label}</span>
			<span className={cn("font-medium", highlight && "text-destructive")}>{value}</span>
		</div>
	)
}

function fmtDate(d: Date | string) {
	return new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })
}

function isExpiringSoon(d: Date | string) {
	const ms = new Date(d).getTime() - Date.now()
	return ms > 0 && ms < 30 * 24 * 60 * 60 * 1000
}
